import FileUploadForm from "components/PLCTABS/CompanyOfficialsTab/FileUploadForm";
import Checkbox from "components/lib/Shared/Checkbox";
import DatePickerField from "components/lib/Shared/DatePickerField";
import ReactSelectWithFormik from "components/lib/Shared/DropdownSelect";
import { format, lastDayOfMonth, parse } from "date-fns";
import { Field } from "formik";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";

const FinancialStatements = ({
  field,
  form: {
    values,
    errors,
    touched,
    setFieldValue,
    validateField,
    setFieldTouched,
    isSubmitting,
  },
  transformedComments,
  filteredFinancialYearOptions,
  yearOptions,
  lastFinancialStatementYear,
}) => {
  const { companyFormation, company } = useSelector(
    (state) => state.companySlice
  );

  // State to track availability for each financial statement
  const [availability, setAvailability] = useState(
    values?.financialStatements?.statements?.map((statement) => ({
      isAvailable: !statement.isNotAvailable,
      isNotAvailable: statement.isNotAvailable || false,
    })) || []
  );

  const documentData = [
    {
      label: "Financial Statements",
      name: "financialStatements",
      accept: "application/pdf",
    },
  ];

  const accountingEndMonth =
    company?.primaryBusinessActivity?.accountingEndMonth || "";

  // Use selectedYear for display purposes
  const selectedYear =
    values?.financialStatements?.lastFinancialStatementYear ||
    lastFinancialStatementYear;

  // Sync statements and availability when filteredYearOptions change
  useEffect(() => {
    // Only reset statements if we're in edit mode and need to update
    if (
      filteredFinancialYearOptions?.length > 0 &&
      (!values?.financialStatements?.statements ||
        values?.financialStatements?.statements.length === 0)
    ) {
      const newStatements = filteredFinancialYearOptions.map((option) => {
        const yearMonth = `${option.value}/${accountingEndMonth}`;
        const lastDayDate = lastDayOfMonth(new Date(yearMonth));
        return {
          year: option.value.toString(),
          isNotAvailable: false,
          yearOfStatement: format(
            new Date(
              lastDayOfMonth(new Date(`${option.value}/${accountingEndMonth}`))
            ),
            "yyyy/MM/dd"
          ),
          boardApprovalDate: "",
          auditorApprovalDate: "",
          fileUploads: [],
        };
      });

      setFieldValue("financialStatements.statements", newStatements);
      setAvailability(
        newStatements.map((statement) => ({
          isAvailable: !statement.isNotAvailable,
          isNotAvailable: statement.isNotAvailable,
        }))
      );
    }
  }, [
    selectedYear,
    setFieldValue,
    filteredFinancialYearOptions,
    accountingEndMonth,
    values?.financialStatements?.statements,
  ]);

  // Set touched for lastFinancialStatementYear and fileUploads
  useEffect(() => {
    if (values?.financialStatements?.lastFinancialStatementYear) {
      setFieldTouched("financialStatements.lastFinancialStatementYear", true);
    }
    values?.financialStatements?.statements?.forEach((_, index) => {
      const fileUploadsName = `financialStatements.statements[${index}].fileUploads`;
      const fileUploads =
        values?.financialStatements?.statements[index]?.fileUploads;
      if (
        fileUploads?.financialStatements &&
        !values?.financialStatements?.statements[index]?.isNotAvailable
      ) {
        setFieldTouched(fileUploadsName, true);
        validateField(fileUploadsName);
      }
    });
  }, [
    values?.financialStatements?.lastFinancialStatementYear,
    values?.financialStatements?.statements,
    setFieldTouched,
    validateField,
  ]);

  const handleAvailabilityChange = (index, isNotAvailable) => {
    const newAvailability = [...availability];
    const newStatements = [...(values?.financialStatements?.statements || [])];
    if (isNotAvailable) {
      // Set current and subsequent years to not available
      for (let i = index; i < newAvailability.length; i++) {
        newAvailability[i] = { isAvailable: false, isNotAvailable: true };
        newStatements[i] = {
          ...newStatements[i],
          isNotAvailable: true,
          yearOfStatement: "",
          fileUploads: [],
          boardApprovalDate: "",
          auditorApprovalDate: "",
        };
      }
    } else {
      // Reset all to available
      newAvailability.fill({ isAvailable: true, isNotAvailable: false });
      newStatements.forEach((statement, i) => {
        statement.isNotAvailable = false;
        statement.yearOfStatement = format(
          new Date(`${statement.year}/${accountingEndMonth}`),
          "yyyy/MM"
        );
      });
    }
    setAvailability(newAvailability);
    setFieldValue("financialStatements.statements", newStatements);
  };

  // Helper function to get minDate and maxDate for date pickers
  const getDateConstraints = (index) => {
    const boardApprovalDate =
      values?.financialStatements?.statements[index]?.boardApprovalDate;
    const auditorApprovalDate =
      values?.financialStatements?.statements[index]?.auditorApprovalDate;

    let boardMinDate = null;
    let boardMaxDate = null;
    let auditorMinDate = null;
    let auditorMaxDate = null;

    if (auditorApprovalDate) {
      // If auditor date is selected, board date must be on or before it
      boardMaxDate = parse(auditorApprovalDate, "yyyy/MM/dd", new Date());
    }
    if (boardApprovalDate) {
      // If board date is selected, auditor date must be on or after it
      auditorMinDate = parse(boardApprovalDate, "yyyy/MM/dd", new Date());
    }

    return {
      boardMinDate,
      boardMaxDate,
      auditorMinDate,
      auditorMaxDate,
      boardApprovalDate,
      auditorApprovalDate,
    };
  };

  return (
    <div>
      <div className="flex items-baseline justify-between m-4">
        <label
          htmlFor={field.name}
          className="block mb-2 text-sm text-primary font-bold min-w-[13rem]  w-[30%] whitespace-normal"
        >
          {`Year of ${
            company?.financialStatements || []?.length > 0 ? "last" : "first"
          } Financial Statement`}
        </label>
        {lastFinancialStatementYear &&
        company?.financialStatements?.length === 0 ? (
          <div className="bg-formInput block  w-[70%] p-2 text-sm rounded-md">
            <Field
              name="financialStatements.lastFinancialStatementYear"
              component={ReactSelectWithFormik}
              options={yearOptions}
              errorPath="financialStatements.lastFinancialStatementYear"
              placeholder={`Select year of ${
                parseInt(format(new Date(company?.registrationDate), "yyyy")) +
                1
                  ? "first"
                  : "last"
              } financial statement`}
              onBlur={field.onBlur}
              onChange={(option) => {
                setFieldValue(
                  "financialStatements.lastFinancialStatementYear",
                  option.value
                );
              }}
              isDisabled={field.disabled}
            />
          </div>
        ) : (
          <div
            className={`bg-formInput flex w-[80%] justify-between relative p-2`}
          >
            {lastFinancialStatementYear}
          </div>
        )}
      </div>
      {filteredFinancialYearOptions?.map((option, index) => {
        // Safety check to ensure statement exists
        if (!values?.financialStatements?.statements?.[index]) {
          return <div>Loading...</div>; // or loading state
        }

        const fileUploadsName = `financialStatements.statements[${index}].fileUploads`;
        const boardApprovalDateName = `financialStatements.statements[${index}].boardApprovalDate`;
        const auditorApprovalDateName = `financialStatements.statements[${index}].auditorApprovalDate`;

        const {
          boardMinDate,
          boardMaxDate,
          auditorMinDate,
          auditorMaxDate,
          boardApprovalDate,
          auditorApprovalDate,
        } = getDateConstraints(index);

        return (
          <div
            key={option.value}
            className="shadow-sm sm:rounded-md sm:overflow-hidden px-4 py-6 space-y-4 bg-gray-50 sm:p-6 mt-4"
          >
            <div className=" mb-4">
              <h2 className="text-4xl font-bold text-gray-800">
                {option.label}
              </h2>
              <p className="text-gray-800 font-bold">Financial Statements</p>
            </div>
            <div className="flex justify-between">
              <div className="w-[85%]">
                <Field
                  name={fileUploadsName}
                  component={FileUploadForm}
                  documentData={documentData}
                  setFieldValue={(name, value) => {
                    setFieldValue(name, value);
                    setFieldTouched(name, true);
                    validateField(name);
                  }}
                  setFieldTouched={(name) => {
                    setFieldTouched(name, true);
                    validateField(name);
                  }}
                  disabled={availability[index]?.isNotAvailable}
                  showRequiredAsterisk
                  showModeType={values?.financialStatements?.statements?.find(
                    (statement, indexValue) =>
                      indexValue === index &&
                      statement?.fileUploads?.financialStatements
                  )}
                />
                <ValidationError
                  errorMessage={
                    errors?.financialStatements?.statements?.[index]
                      ?.fileUploads
                  }
                  isTouched={
                    touched?.financialStatements?.statements?.[index]
                      ?.fileUploads
                  }
                />
              </div>
              <div className="flex flex-col items-start justify-start">
                <p className="text-green-focused font-medium text-sm">
                  Available
                </p>
                <div className="flex gap-2">
                  <Checkbox
                    name={`availability[${index}].isAvailable`}
                    label="Yes"
                    checked={availability[index]?.isAvailable}
                    onChange={() => handleAvailabilityChange(index, false)}
                    //disabled={index < availability.findIndex((a) => a.isNotAvailable)}
                  />
                  <Checkbox
                    name={`availability[${index}].isNotAvailable`}
                    label="No"
                    checked={availability[index]?.isNotAvailable}
                    onChange={() => handleAvailabilityChange(index, true)}
                    //disabled={index < availability.findIndex((a) => a.isNotAvailable)}
                  />
                </div>
              </div>
            </div>

            <div>
              <DatePickerField
                label="Board Approval Date"
                onChange={(date) => {
                  if (!date) {
                    setFieldValue(boardApprovalDateName, "");
                    setFieldTouched(boardApprovalDateName, true);
                    validateField(boardApprovalDateName);
                    return;
                  }
                  if (isNaN(date.getTime())) {
                    console.error("Invalid date:", date);
                    return;
                  }
                  const formattedDate = format(date, "yyyy/MM/dd");
                  setFieldValue(boardApprovalDateName, formattedDate);
                  setFieldTouched(boardApprovalDateName, true);
                  validateField(boardApprovalDateName);

                  // If auditor date is set and is before the new board date, clear it
                  if (
                    auditorApprovalDate &&
                    parse(auditorApprovalDate, "yyyy/MM/dd", new Date()) < date
                  ) {
                    setFieldValue(auditorApprovalDateName, "");
                    setFieldTouched(auditorApprovalDateName, true);
                    validateField(auditorApprovalDateName);
                  }
                }}
                selected={
                  values?.financialStatements?.statements[index]
                    ?.boardApprovalDate
                    ? new Date(
                        values?.financialStatements?.statements[
                          index
                        ].boardApprovalDate
                      )
                    : null
                }
                name={boardApprovalDateName}
                placeholderText="Enter Board Approval Date"
                errorPath={boardApprovalDateName}
                minDate={boardMinDate}
                maxDate={boardMaxDate}
                disabled={isSubmitting || availability[index]?.isNotAvailable}
                showRequiredAsterisk
              />
              <ValidationError
                errorMessage={
                  errors?.financialStatements?.statements?.[index]
                    ?.boardApprovalDate
                }
                isTouched={
                  touched?.financialStatements?.statements?.[index]
                    ?.boardApprovalDate
                }
              />
            </div>
            <div>
              <DatePickerField
                label="Auditor Approval Date"
                onChange={(date) => {
                  if (!date) {
                    setFieldValue(auditorApprovalDateName, "");
                    setFieldTouched(auditorApprovalDateName, true);
                    validateField(auditorApprovalDateName);
                    return;
                  }
                  if (isNaN(date.getTime())) {
                    console.error("Invalid date:", date);
                    return;
                  }
                  const formattedDate = format(date, "yyyy/MM/dd");
                  setFieldValue(auditorApprovalDateName, formattedDate);
                  setFieldTouched(auditorApprovalDateName, true);
                  validateField(auditorApprovalDateName);

                  // If board date is set and is after the new auditor date, clear it
                  if (
                    boardApprovalDate &&
                    parse(boardApprovalDate, "yyyy/MM/dd", new Date()) > date
                  ) {
                    setFieldValue(boardApprovalDateName, "");
                    setFieldTouched(boardApprovalDateName, true);
                    validateField(boardApprovalDateName);
                  }
                }}
                selected={
                  values?.financialStatements?.statements[index]
                    ?.auditorApprovalDate
                    ? new Date(
                        values?.financialStatements?.statements[
                          index
                        ].auditorApprovalDate
                      )
                    : null
                }
                name={auditorApprovalDateName}
                placeholderText="Enter Auditor Approval Date"
                errorPath={auditorApprovalDateName}
                minDate={auditorMinDate}
                maxDate={auditorMaxDate}
                disabled={isSubmitting || availability[index]?.isNotAvailable}
                showRequiredAsterisk
              />
              <ValidationError
                errorMessage={
                  errors?.financialStatements?.statements?.[index]
                    ?.auditorApprovalDate
                }
                isTouched={
                  touched?.financialStatements?.statements?.[index]
                    ?.auditorApprovalDate
                }
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default FinancialStatements;

const ValidationError = ({ errorMessage, isTouched }) => {
  if (!errorMessage || !isTouched) return null;

  return (
    <>
      <div className="text-red-500 flex items-center py-2">
        <svg
          aria-hidden="true"
          className="flex-shrink-0 inline w-5 h-5 mr-1"
          fill="currentColor"
          viewBox="0 0 20 20"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fillRule="evenodd"
            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
            clipRule="evenodd"
          ></path>
        </svg>

        <p className="text-sm">{errorMessage}</p>
      </div>
    </>
  );
};
